<script setup lang="ts">
import type {
  TypeProductDetailsPageSkeleton,
  TypeProductDetailsPageWithoutUnresolvableLinksResponse,
  TypeProductSkeleton,
  TypeProductWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { CustomElementsDependencies } from '~/types/custom-elements'
import ProductCard from '~/components/ProductCard.vue'
import ProductCardSkeleton from '~/components/ProductCardSkeleton.vue'

declare global {
  interface Window {
    __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
  }
}

const props = defineProps<{
  entry: string
  parentProduct?: string
  debug?: boolean | string
}>()

const product = ref<TypeProductWithoutUnresolvableLinksResponse>()
const productPage =
  ref<TypeProductDetailsPageWithoutUnresolvableLinksResponse>()
const isLoading = ref(false)

const isDebug = computed(() => {
  return (
    props.debug === '' ||
    props.debug === true ||
    props.debug === 'true' ||
    props.debug === '1'
  )
})

const shouldRender = computed(() => {
  return Boolean(product.value && productPage.value && computedHref.value)
})

const computedHref = computed(() => {
  const locale = window.__WPN_CUSTOM_ELEMENTS__?.getLocale?.() || 'en'
  const path = productPage.value?.fields?.path
  const productId = product.value?.sys?.id

  if (!path || !productId) return ''

  return `/${locale}/products/${path}?product=${encodeURIComponent(productId)}`
})

const debugLog = (...args: unknown[]) => {
  if (isDebug.value) {
    console.log('[ProductCard]', ...args)
  }
}

async function fetchProduct(
  deps: CustomElementsDependencies,
  locale: string,
  entryId: string,
) {
  const response =
    await deps.client.withoutUnresolvableLinks.getEntries<TypeProductSkeleton>({
      content_type: 'product',
      include: 2,
      limit: 1,
      'sys.id': entryId,
      locale,
    })

  return response.items[0]
}

async function findParentProduct(
  deps: CustomElementsDependencies,
  locale: string,
) {
  if (props.parentProduct) {
    const parentFound = await fetchProduct(deps, locale, props.parentProduct)

    if (!parentFound) {
      debugLog('parent product not found', {
        entryId: props.entry,
        parentProduct: props.parentProduct,
        locale,
      })
    }

    return parentFound
  }

  const response =
    await deps.client.withoutUnresolvableLinks.getEntries<TypeProductSkeleton>({
      content_type: 'product',
      include: 2,
      limit: 2,
      locale,
      'fields.variants.sys.id[in]': [props.entry],
    })

  if (response.items.length > 1) {
    debugLog('multiple parent products found; using first result', {
      entryId: props.entry,
      parentProductIds: response.items.map((item) => item.sys.id),
    })
  }

  return response.items[0]
}

async function findProductDetailsPage(
  deps: CustomElementsDependencies,
  locale: string,
  productId: string,
) {
  const response =
    await deps.client.withoutUnresolvableLinks.getEntries<TypeProductDetailsPageSkeleton>(
      {
        content_type: 'productDetailsPage',
        include: 2,
        limit: 1,
        locale,
        'fields.products.sys.id[in]': [productId],
        select: [
          'sys.id',
          'fields.path',
          'fields.visibility',
          'fields.accessLevel',
        ],
      },
    )

  return response.items[0]
}

async function loadProductCard() {
  product.value = undefined
  productPage.value = undefined

  if (!props.entry) {
    debugLog('missing required "entry" prop')
    return
  }

  if (!window.__WPN_CUSTOM_ELEMENTS__) {
    debugLog('custom element dependencies are unavailable')
    return
  }

  isLoading.value = true

  try {
    const deps = window.__WPN_CUSTOM_ELEMENTS__
    const locale = deps.getLocale()

    debugLog('locale', locale)

    const productFound = await fetchProduct(deps, locale, props.entry)

    if (!productFound) {
      debugLog('no product found', { entryId: props.entry, locale })
      return
    }

    const parentProductFound = await findParentProduct(deps, locale)
    const pageProductId = parentProductFound?.sys?.id || props.entry
    const pageFound = await findProductDetailsPage(deps, locale, pageProductId)

    if (!pageFound) {
      debugLog('no product details page found', {
        entryId: props.entry,
        pageProductId,
        locale,
      })
      return
    }

    const productVisibility = deps.visibilityDecision(
      productFound.fields?.visibility,
      { debug: isDebug.value },
    )
    const parentProductVisibility = parentProductFound
      ? deps.visibilityDecision(parentProductFound.fields?.visibility, {
          debug: isDebug.value,
        })
      : undefined
    const pageVisibility = deps.visibilityDecision(
      pageFound.fields?.visibility,
      { debug: isDebug.value },
    )

    debugLog('visibility decision', {
      entryId: props.entry,
      productVisibility,
      parentProductVisibility,
      pageVisibility,
    })

    if (
      productVisibility.visible !== true ||
      (parentProductVisibility && parentProductVisibility.visible !== true) ||
      pageVisibility.visible !== true
    ) {
      debugLog('visibility prevented render', {
        entryId: props.entry,
        productVisibility,
        parentProductVisibility,
        pageVisibility,
      })
      return
    }

    const highestUserLevel = deps.getHighestUserLevel()
    const hasProductAccess = deps.userAccessCheck(
      productFound.fields?.accessLevel,
      highestUserLevel,
    )
    const hasParentProductAccess = parentProductFound
      ? deps.userAccessCheck(
          parentProductFound.fields?.accessLevel,
          highestUserLevel,
        )
      : true
    const hasPageAccess = deps.userAccessCheck(
      pageFound.fields?.accessLevel,
      highestUserLevel,
    )

    if (!hasProductAccess || !hasParentProductAccess || !hasPageAccess) {
      debugLog('access prevented render', {
        entryId: props.entry,
        hasProductAccess,
        hasParentProductAccess,
        hasPageAccess,
      })
      return
    }

    product.value = productFound
    productPage.value = pageFound

    debugLog('rendering product card', {
      entryId: props.entry,
      href: computedHref.value,
    })
  } finally {
    isLoading.value = false
  }
}

watch(
  () => [props.entry, props.parentProduct],
  () => {
    loadProductCard()
  },
  { immediate: true },
)
</script>

<template>
  <ProductCard v-if="shouldRender" :data="product" :href="computedHref" />
  <ProductCardSkeleton v-else-if="isLoading" />
</template>
